import { jwtDecode, JwtPayload } from "jwt-decode";
import { createContext, useCallback, useContext, useEffect, useReducer, useState } from "react";
import { flushSync } from "react-dom";
import axios from "axios";
import { useQueryClient } from "@tanstack/react-query";
import {
    clearAuthStorage,
    getStoredFederation,
    POST_LOGOUT_RETURN_SESSION_KEY,
    promotePendingOAuthFederation,
    requestLogoutApi,
    STORAGE_KEYS,
} from "../services/logoutSession";
import { SessionLogoutOverlay } from "../layout/MainLoader";

interface IAuthProviderProps {
    children: React.ReactNode;
}

interface IUserState {
    user_id: string;
    name: string;
    email: string;
}

interface IAuthState {
    isAuthenticated: boolean;
    isLoading: boolean;
    token: string;
    user: IUserState;
}

interface IAuthContext extends IAuthState {
    login: (token: string) => void;
    logout: () => Promise<void>;
    refreshingToken: boolean;
    isLoggingOut: boolean;
}

const AuthContext = createContext<IAuthContext|null>(null);

const initialState: IAuthState = {
    isAuthenticated: false,
    isLoading: true,
    token: "",
    user: {
        user_id: "",
        name: "",
        email: ""
    }
}

const reducer = (state: IAuthState, action: { type: string; payload: { token: string|null; user: IUserState|null; }|null; }) => {
    switch (action.type) {
        case "Login":
            return {
                ...state,
                isAuthenticated: true,
                token: action.payload!.token!,
                user: action.payload!.user!,
                isLoading: false
            }
        case "Logout":
            return {
                ...initialState,
                isLoading: false
            }
        default:
            throw new Error(`Type ${action.type} is not implemented.`);
    }
}

const AuthProvider = ({ children }: IAuthProviderProps) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const [refreshingToken, setRefreshingToken] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const queryClient = useQueryClient();

    useEffect(() => {
        const validateToken = async () => {
            const hash = window.location.hash;
            const urlParams = new URLSearchParams(hash.replace('#', '?'));
        
            if (urlParams.get("access_token")) {
                localStorage.setItem(STORAGE_KEYS.TOKEN, urlParams.get("id_token")!);
                localStorage.setItem(STORAGE_KEYS.REFRESH, urlParams.get("refresh_token")!);
                promotePendingOAuthFederation();
            }

            const tokenAfterHash = localStorage.getItem(STORAGE_KEYS.TOKEN);
            if (
                sessionStorage.getItem(POST_LOGOUT_RETURN_SESSION_KEY) === "1" &&
                !tokenAfterHash
            ) {
                sessionStorage.removeItem(POST_LOGOUT_RETURN_SESSION_KEY);
                dispatch({ type: "Logout", payload: null });
                return;
            }

            const accessToken = tokenAfterHash;

            try {
                if (!accessToken) {
                    throw new Error('Token não encontrado');
                }
        
                const decodedToken = jwtDecode(accessToken, { header: true });

                const keys = (await (await fetch(`${import.meta.env.VITE_COGNITO_URL}/.well-known/jwks.json`, {
                    method: 'GET'
                })).json()).keys;
        
                const key = keys.find((key: { kid: string | undefined; }) => key.kid === decodedToken.kid);
        
                if (!key) {
                    throw new Error('Chave pública não encontrada.');
                }
        
                const verified: JwtPayload&IUserState = jwtDecode(accessToken);
        
                if ((verified.exp ?? 0) * 1000 < Date.now()) {
                    throw new Error('Token expirado');
                }
        
                if (verified.iss !== `${import.meta.env.VITE_COGNITO_URL}`) {
                    throw new Error('Token inválido');
                }
    
                if (verified) {
                    const user = {
                        user_id: verified.user_id,
                        name: verified.name,
                        email: verified.email
                    };

                    setTimeout(() => {
                        dispatch({ type: "Login", payload: { token: accessToken!, user } });
                    }, 1000);
                } else {
                    throw new Error("Usuário não autenticado.");
                }
            } catch (err) {
                console.error(err);
                dispatch({ type: "Logout", payload: null });
            }
        }

        validateToken().then();
    }, []);

    useEffect(() => {
        const interval = setInterval(async () => {
            const idToken = localStorage.getItem(STORAGE_KEYS.TOKEN);
            if (!idToken) {
                dispatch({ type: "Logout", payload: null });
                return null;
            }

            try {
                const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH);
                if (!refreshToken) {
                    return null;
                }
        
                setRefreshingToken(true);
                const result = (await axios.get<{ data: { id_token: string; } }>(`${import.meta.env.VITE_API_URL}/refresh-token?refresh_token=${refreshToken}`, {
                    headers: {
                        Authorization: idToken
                    }
                })).data;
        
                localStorage.setItem(STORAGE_KEYS.TOKEN, result?.data.id_token ?? '');

                return result;
            } catch {
                dispatch({ type: "Logout", payload: null });
            } finally {
                setRefreshingToken(false);
            }
        }, (1000 * 60) * 60);

        return () => clearInterval(interval);
    }, []);

    const login = (token: string) => {
        const verified: JwtPayload&IUserState = jwtDecode(token);

        const user = {
            user_id: verified.user_id,
            name: verified.name,
            email: verified.email
        };

        dispatch({ type: "Login", payload: { token: token!, user } });
    }

    const logout = useCallback(async () => {
        flushSync(() => {
            setIsLoggingOut(true);
        });

        const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH);
        const accessToken = localStorage.getItem(STORAGE_KEYS.TOKEN);
        const federation = getStoredFederation() ?? "google";

        let hostedUrl: string | null = null;
        try {
            if (refreshToken || accessToken) {
                const result = await requestLogoutApi({
                    refreshToken,
                    accessToken,
                    federation,
                });
                hostedUrl = result.hostedUrl;
            }
        } catch {
            /* still clear local session; API may be down or tokens already invalid */
        }

        clearAuthStorage();
        queryClient.clear();

        try {
            if (hostedUrl) {
                window.location.assign(hostedUrl);
            } else {
                sessionStorage.setItem(POST_LOGOUT_RETURN_SESSION_KEY, "1");
                window.location.assign("/");
            }
        } catch {
            setIsLoggingOut(false);
            dispatch({ type: "Logout", payload: null });
        }
    }, [queryClient]);

    return (
        <AuthContext.Provider value={{ ...state, login, logout, refreshingToken, isLoggingOut }}>
            {isLoggingOut ? <SessionLogoutOverlay /> : null}
            {children}
        </AuthContext.Provider>
    );
}

const useAuthContext = () => {
    const context = useContext(AuthContext);

    if (!context)
        throw new Error(`AuthContext cannot be null`);

    return context;
}

export { AuthProvider, useAuthContext }
